import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ExternalLink, Copy, Loader2, Presentation } from "lucide-react";
import { toast } from "sonner";
import { useResourceWorkshops, useWorkshopMutations } from "@/hooks/useResourceWorkshops";

export default function ResourceWorkshopsListPage() {
  const navigate = useNavigate();
  const { data: workshops, isLoading } = useResourceWorkshops();
  const { remove } = useWorkshopMutations();
  const [toDelete, setToDelete] = useState<{ id: string; name: string } | null>(null);

  const copyLink = (slug: string) => {
    const url = `${window.location.origin}/taller/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado", { description: url });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Presentation className="w-6 h-6 text-primary" /> Talleres
          </h1>
          <p className="text-sm text-muted-foreground">
            Microsites con recursos compartibles. Cada taller se publica en <code>/taller/&lt;slug&gt;</code>.
          </p>
        </div>
        <Button onClick={() => navigate("/admin/talleres/new")} className="gap-2">
          <Plus className="w-4 h-4" /> Nuevo Taller
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Talleres creados</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : !workshops?.length ? (
            <p className="text-center text-muted-foreground py-8">No hay talleres aún. Crea el primero.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Visibilidad</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Recursos</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workshops.map((w: any) => (
                  <TableRow key={w.id}>
                    <TableCell className="font-medium">{w.name}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded">{w.slug}</code>
                    </TableCell>
                    <TableCell>
                      <Badge variant={w.visibility === "public" ? "default" : "secondary"}>
                        {w.visibility === "public" ? "Pública" : "Solo registrados"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={w.is_active ? "default" : "outline"}>
                        {w.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                    </TableCell>
                    <TableCell>{w.items?.[0]?.count ?? 0}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="icon" variant="ghost" onClick={() => copyLink(w.slug)} title="Copiar link público">
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => window.open(`/taller/${w.slug}`, "_blank")} title="Abrir">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => navigate(`/admin/talleres/${w.id}`)} title="Editar">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setToDelete({ id: w.id, name: w.name })} title="Eliminar">
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar taller?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará "{toDelete?.name}" y todos sus recursos. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!toDelete) return;
                try {
                  await remove.mutateAsync(toDelete.id);
                  toast.success("Taller eliminado");
                  setToDelete(null);
                } catch (e: any) {
                  toast.error("Error", { description: e.message });
                }
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
